/**
 * UserMenu - User avatar and dropdown menu for Header.
 * Hosts that keep the bar slim (field crew) pass onToggleTheme so dark mode
 * is a row here instead of a bar button.
 */

import { useState, useRef } from 'react'
import { useCloseOnOutsidePointer } from '../../hooks/useCloseOnOutsidePointer'
import { Bell, Download, Keyboard, LogOut, Moon, Palette, Search, Settings, Shield, Sun } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { isDesktopApp } from '../../lib/utils'
import type { ProfileAlias, User } from '../types'

// Public download page (Mac .dmg + Windows installer). Hosted on the
// downloads CDN; see docs/agents notebook + .agent-credentials.json.
const DESKTOP_APP_DOWNLOAD_URL = 'https://download.quotech.ai/'

type UserMenuUser = User & {
  /** Header-only authority display; never an auth/send-as identity. */
  displayEmail?: string
}

interface UserMenuProps {
  user: UserMenuUser
  onLogout?: () => void
  /** Present on the master shell. Tenant hubs omit it and keep a read-only alias. */
  onUseProfile?: (alias: ProfileAlias) => void
  /** Opens the universal settings modal on a tab. Omitted hosts stay sign-out only. */
  onOpenSettings?: (tabId?: string) => void
  theme?: 'light' | 'dark'
  /** When set, the menu carries the dark mode row. */
  onToggleTheme?: () => void
  /** Phone rows for controls the header hides when a page owns the row
   *  (Header `mobileNavHost`). Shown below 640px only. */
  phoneExtras?: {
    onOpenSearch?: () => void
    theme?: 'light' | 'dark'
    onToggleTheme?: () => void
  }
}

const ROW = 'w-full flex items-center gap-2 px-3 py-2.5 text-xs text-foreground hover:bg-muted touch-manipulation'

function MenuRow({ icon: Icon, label, onClick, className = '' }: {
  icon: LucideIcon
  label: string
  onClick: () => void
  className?: string
}) {
  return (
    <button type="button" onClick={onClick} className={`${ROW} ${className}`}>
      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      <span>{label}</span>
    </button>
  )
}

const themeIcon = (theme?: 'light' | 'dark') => (theme === 'dark' ? Sun : Moon)
const themeLabel = (theme?: 'light' | 'dark') => (theme === 'dark' ? 'Light mode' : 'Dark mode')

const ACCOUNT_ROWS: readonly { tab: string; label: string; icon: LucideIcon }[] = [
  { tab: 'profile', label: 'Settings', icon: Settings },
  { tab: 'appearance', label: 'Appearance', icon: Palette },
  { tab: 'notifications', label: 'Notifications', icon: Bell },
  { tab: 'security', label: 'Password and security', icon: Shield },
  { tab: 'keyboard', label: 'Shortcuts', icon: Keyboard },
]

export function UserMenu({ user, onLogout, onUseProfile, onOpenSettings, theme, onToggleTheme, phoneExtras }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const aliases = user.aliases ?? []

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [ref])

  return (
    <div ref={ref} className="relative ml-1">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center gap-1 p-1.5 md:p-0.5 rounded-full hover:ring-2 hover:ring-border transition-all touch-manipulation"
      >
        {user.avatar ? (
          <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-border flex items-center justify-center">
            <span className="text-xs font-medium text-muted-foreground">
              {(user.name || user.email || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-72 bg-popover border border-border rounded-lg shadow-lg z-50 py-1">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-medium text-foreground truncate">{user.name}</p>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Primary</p>
            <p className="text-[10px] text-slate-400 truncate">{user.email}</p>
          </div>
          {aliases.map((alias) => (
            <div key={String(alias.user_id)} className="px-3 py-2 border-b border-border">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Alias</p>
              <p className="text-[10px] text-foreground truncate">{alias.email}</p>
              {alias.organisation_name ? (
                <p className="text-[10px] text-slate-400 truncate">{alias.organisation_name}</p>
              ) : null}
              {onUseProfile ? (
                <button
                  type="button"
                  onClick={() => { setOpen(false); onUseProfile(alias) }}
                  className="mt-1 text-[10px] font-medium text-teal-700 dark:text-teal-300 hover:underline"
                >
                  Use this profile
                </button>
              ) : null}
            </div>
          ))}
          {phoneExtras && (phoneExtras.onOpenSearch || phoneExtras.onToggleTheme) && (
            <div className="border-b border-border py-1 sm:hidden" data-quotech-user-menu-phone="">
              {phoneExtras.onOpenSearch && (
                <MenuRow icon={Search} label="Search" onClick={() => { setOpen(false); phoneExtras.onOpenSearch?.() }} />
              )}
              {phoneExtras.onToggleTheme && (
                <MenuRow
                  icon={themeIcon(phoneExtras.theme)}
                  label={themeLabel(phoneExtras.theme)}
                  onClick={() => { setOpen(false); phoneExtras.onToggleTheme?.() }}
                />
              )}
            </div>
          )}
          {onToggleTheme && (
            <MenuRow
              icon={themeIcon(theme)}
              label={themeLabel(theme)}
              onClick={() => { setOpen(false); onToggleTheme() }}
              className="md:py-1.5 border-b border-border"
            />
          )}
          {onOpenSettings && (
            <div className="border-b border-border py-1">
              {ACCOUNT_ROWS.map((row) => {
                const Icon = row.icon
                return (
                  <button
                    key={row.tab}
                    type="button"
                    onClick={() => { setOpen(false); onOpenSettings(row.tab) }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 md:py-1.5 text-xs text-foreground hover:bg-muted touch-manipulation"
                  >
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{row.label}</span>
                  </button>
                )
              })}
            </div>
          )}
          {/* Surface the native desktop app to web users (offline email,
              multi-window across screens). Hidden when we're already running
              inside the desktop shell - no point offering it to itself. */}
          {!isDesktopApp() && (
            <a
              href={DESKTOP_APP_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2.5 md:py-1.5 text-xs text-muted-foreground hover:bg-muted touch-manipulation border-b border-border"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download desktop app</span>
            </a>
          )}
          <button
            onClick={() => { setOpen(false); onLogout?.() }}
            className="w-full flex items-center gap-2 px-3 py-2.5 md:py-1.5 text-xs text-muted-foreground hover:bg-muted touch-manipulation"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  )
}
